/**
 * Reduce una imagen en el navegador antes de subirla: baja la resolucion y la
 * recomprime hasta que pese menos de `maxBytes`.
 *
 * El backend no configura spring.servlet.multipart, asi que rige el tope por
 * defecto de Spring: 1 MB por archivo. Una foto de celular pesa 3-10 MB; con
 * 2000 px en el lado mayor sigue viendose bien (producto, logo, banner, captura
 * de Yape) y entra de sobra.
 *
 * - No toca lo que no es imagen, ni GIF (animado) ni SVG (vectorial).
 * - Una imagen con transparencia (logo PNG) sigue siendo PNG: solo se achica.
 *   Sin transparencia se pasa a JPEG, que pesa mucho menos.
 */
export interface ImageCompressOptions {
    maxBytes?: number;
    maxDimension?: number;
}

/** Lo que se sube al backend; por debajo del tope de 1 MB de Spring. */
export const MAX_BYTES_IMAGEN_SUBIDA = 900_000;
/** Lo que se deja seleccionar: la imagen se reduce antes de subirla. */
export const MAX_BYTES_IMAGEN_SELECCION = 10_000_000;

const CALIDADES = [0.85, 0.75, 0.65, 0.55];
const MIN_DIMENSION = 600;
const NO_COMPRIMIBLES = ['image/gif', 'image/svg+xml'];

export const esImagenComprimible = (file: Blob): boolean =>
    !!file?.type && file.type.startsWith('image/') && !NO_COMPRIMIBLES.includes(file.type);

export const compressImage = async (file: File, options: ImageCompressOptions = {}): Promise<File> => {
    const maxBytes = options.maxBytes || MAX_BYTES_IMAGEN_SUBIDA;
    if (!esImagenComprimible(file) || file.size <= maxBytes) {
        return file;
    }
    const image = await loadImage(file);
    const conTransparencia = (file.type === 'image/png' || file.type === 'image/webp') && tieneTransparencia(image);
    const tipo = conTransparencia ? 'image/png' : 'image/jpeg';
    // PNG no tiene calidad: solo se puede achicar la resolucion.
    const calidades = conTransparencia ? [undefined] : CALIDADES;

    let maxDimension = options.maxDimension || 2000;
    while (maxDimension >= MIN_DIMENSION) {
        for (const calidad of calidades) {
            const blob = await render(image, maxDimension, tipo, calidad);
            if (blob.size <= maxBytes) {
                const extension = conTransparencia ? '.png' : '.jpg';
                return new File([blob], file.name.replace(/\.[^.]+$/, '') + extension, {type: tipo});
            }
        }
        maxDimension = Math.round(maxDimension * 0.75);
    }
    throw new Error('No se pudo reducir la imagen lo suficiente.');
};

const loadImage = (file: File): Promise<HTMLImageElement> => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
    };
    image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No se pudo leer la imagen.'));
    };
    image.src = url;
});

/** Mira el canal alfa sobre una copia chica: basta para saber si hay transparencia. */
const tieneTransparencia = (image: HTMLImageElement): boolean => {
    const lado = 256;
    const escala = Math.min(1, lado / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * escala));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * escala));
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
            return true;
        }
    }
    return false;
};

const render = (image: HTMLImageElement, maxDimension: number, tipo: string, calidad?: number): Promise<Blob> => {
    const escala = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(image.naturalWidth * escala);
    canvas.height = Math.round(image.naturalHeight * escala);
    const context = canvas.getContext('2d');
    if (tipo === 'image/jpeg') {
        // JPEG no tiene alfa: sin fondo, lo transparente saldria negro.
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve, reject) => canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('No se pudo comprimir la imagen.')),
        tipo,
        calidad
    ));
};
